import { computed, unref } from 'vue'

import { useContext } from './context'
import { DateRange } from '@/types/transportation'
import { MaybeRef } from '@/types/types'
import { formatDate, formatDateTime } from '@/utils/date-utils'

/**
 * Composable for formatting date/time periods into human-readable strings
 *
 * This composable takes a date range and formats it into a localized string
 * representation. It handles various scenarios including single dates, date ranges,
 * dates with times, and missing dates.
 *
 * @returns A computed string that represents the formatted date range:
 * - If no start date: returns localized "add a date" message
 * - If start equals end: returns only the start date/time
 * - If date range: returns "start - end" format
 * - Automatically formats as date-only or date-time based on presence of time component
 */
export function useDateTimePeriodFormat(
  dateRange: MaybeRef<DateRange | undefined>,
) {
  const { i18n } = useContext()

  const dateString = computed(() => {
    const period = unref(dateRange)
    if (!period?.start) {
      return i18n.t('addADate').toString()
    }
    let startString = ''
    const [startDate, startTime] = period.start.split('T')
    if (startTime) {
      startString = formatDateTime(period.start)
    } else {
      startString = formatDate(startDate)
    }

    if (period.start === period.end) {
      return startString
    }

    let endString = ''
    if (period.end) {
      const [endDate, endTime] = period.end.split('T')
      if (endTime) {
        endString = formatDateTime(period.end)
      } else {
        endString = formatDate(endDate)
      }
    }

    return `${startString}${endString ? ' - ' + endString : ''}`
  })

  return dateString
}
